import { USER_INVITES, USERS } from '../collections/names'
import { autoDisplayConditionText } from '../constants/consent'
import {
  AutoDisplayCondition,
  ConsentForm,
  ConsentFormTemplate,
  DefaultConsentForms,
  DeliveryLocation,
  InsuranceInfo,
  InsuranceProvider,
  MissingConsentForm,
  PopulatedAssessment,
  TemplateId,
  WithId,
} from '../types'
import { getInitialSendDate } from './assessments'

const isSelfPay = (assessment: PopulatedAssessment) => {
  const insuranceInfo = assessment.mergedData?.insuranceInfo as InsuranceInfo | undefined
  return (
    insuranceInfo?.option === 'no-coverage' &&
    insuranceInfo?.noCoverageDescription === 'out-of-pocket'
  )
}

const hasInsurer = (assessment: PopulatedAssessment, insurerId: string) => {
  const assessmentInsurerId = assessment.plans?.primary?.insuranceProviderId
  if (!assessmentInsurerId) return false
  return assessmentInsurerId === insurerId
}

const hasInsurancePlan = (
  assessment: PopulatedAssessment,
  insurerId: string,
  insurancePlanId: string,
) => {
  const assessmentInsurerId = assessment.plans?.primary?.insuranceProviderId
  if (!assessmentInsurerId) return false
  const assessmentInsurancePlanId = assessment.plans?.primary?.planName
  if (!assessmentInsurancePlanId) return false
  return assessmentInsurerId === insurerId && assessmentInsurancePlanId === insurancePlanId
}

const hasDeliveryLocation = (
  assessment: PopulatedAssessment,
  deliveryLocation: DeliveryLocation,
) => {
  const assessmentDeliveryLocation = assessment.mergedData?.delivery?.deliveryLocation
  if (!assessmentDeliveryLocation) return false
  return assessmentDeliveryLocation === deliveryLocation
}

const shouldDisplay = (assessment: PopulatedAssessment, condition: AutoDisplayCondition) => {
  switch (condition.type) {
    case 'all':
      return true
    case 'medicaid':
      return assessment.plans?.primary?.isMedicaid
    case 'non-medicaid':
      return (
        !!assessment.plans?.primary?.insuranceProviderId && !assessment.plans?.primary?.isMedicaid
      )
    case 'self-pay':
      return isSelfPay(assessment)
    case 'insurer':
      return hasInsurer(assessment, condition.insurerId)
    case 'insurance-plan':
      return hasInsurancePlan(assessment, condition.insurerId, condition.insurancePlanId)
    case 'delivery-location':
      return hasDeliveryLocation(assessment, condition.deliveryLocation)
  }
}

export const getConsentFormAutoDisplayConditions = (
  assessment: PopulatedAssessment,
  consentForm: ConsentFormTemplate,
): AutoDisplayCondition[] => {
  if (!consentForm.autoDisplay || !Array.isArray(consentForm.autoDisplay)) return []
  if (consentForm.autoDisplay.length === 0) return []
  return consentForm.autoDisplay.filter(condition => shouldDisplay(assessment, condition))
}

export const getAutoDisplayConditionKey = (condition: AutoDisplayCondition) => {
  switch (condition.type) {
    case 'all':
      return 'all'
    case 'medicaid':
      return 'medicaid'
    case 'non-medicaid':
      return 'non-medicaid'
    case 'self-pay':
      return 'self-pay'
    case 'insurer':
      return `insurer-${condition.insurerId}`
    case 'insurance-plan':
      return `insurance-plan-${condition.insurerId}-${condition.insurancePlanId}`
  }
}

export const getAutoDisplayConditionText = (
  condition: AutoDisplayCondition,
  insurers: Record<string, InsuranceProvider>,
) => {
  const typeText = autoDisplayConditionText[condition.type]
  if (condition.type === 'insurer') {
    const insurer = insurers[condition.insurerId]
    return `${typeText} ${insurer?.name ?? 'Error: Insurer not found'}`
  }
  if (condition.type === 'insurance-plan') {
    const insurer = insurers[condition.insurerId]
    const matchingPlan = insurer?.plans?.find(plan => plan.name === condition.insurancePlanId)
    return `${typeText} ${insurer?.name ?? 'Error: Insurer not found'} - ${matchingPlan?.name ?? 'Error: Insurance plan not found'}`
  }
  return typeText
}

const getConditionIsOverriddenBy = (
  condition: AutoDisplayCondition,
  checked: AutoDisplayCondition,
) => {
  const conditionKey = getAutoDisplayConditionKey(condition)
  const checkedKey = getAutoDisplayConditionKey(checked)
  // if checked is the same condition, return false
  if (conditionKey === checkedKey) return false
  if (checked.type === 'all') return true
  if (condition.type === 'insurance-plan') {
    // if condition is an insurance plan, it is overridden by an insurer condition with the same insurer
    return checked.type === 'insurer' && checked.insurerId === condition.insurerId
  }
  return false
}

export const getConditionOverrides = (
  conditions: AutoDisplayCondition[],
  condition: AutoDisplayCondition,
) => conditions.filter(c => getConditionIsOverriddenBy(condition, c))

export const getDefaultConsentForms = (
  assessment: PopulatedAssessment,
  practiceId: string | null,
  hbConsentForms: Record<string, ConsentFormTemplate>,
  practiceConsentForms: Record<string, ConsentFormTemplate>,
): DefaultConsentForms => {
  if (!assessment || !practiceConsentForms || !hbConsentForms || !practiceId)
    return {
      practice: [],
      hb: [],
      practiceId: practiceId ?? null,
    }
  const hbDefaultConsentForms = Object.entries(hbConsentForms).reduce<
    Array<{
      form: WithId<ConsentFormTemplate>
      conditions: AutoDisplayCondition[]
      practiceId: null
    }>
  >((acc, [id, form]) => {
    const conditions = getConsentFormAutoDisplayConditions(assessment, form)
    if (conditions.length > 0) {
      acc.push({ form: { id, ...form }, conditions, practiceId: null })
    }
    return acc
  }, [])

  const practiceDefaultConsentForms = Object.entries(practiceConsentForms).reduce<
    Array<{
      form: WithId<ConsentFormTemplate>
      conditions: AutoDisplayCondition[]
      practiceId: string
    }>
  >((acc, [id, form]) => {
    const conditions = getConsentFormAutoDisplayConditions(assessment, form)
    if (conditions.length > 0) {
      acc.push({ form: { id, ...form }, conditions, practiceId })
    }
    return acc
  }, [])

  return {
    hb: hbDefaultConsentForms,
    practice: practiceDefaultConsentForms,
    practiceId,
  }
}

export const stringifyTemplateId = (templateId: TemplateId) =>
  templateId.practiceId
    ? `${templateId.practiceId}-${templateId.templateId}`
    : templateId.templateId

// if called when updating the assessment after assessment sent, we should send only the forms that are missing (ie were not sent after the assessment was sent)
const getShouldSendForm = (
  assessmentSentOn: number | null,
  existingForm: WithId<ConsentForm> | null | undefined,
) => {
  if (!assessmentSentOn) return false
  return !existingForm || existingForm.sentOn < assessmentSentOn - 1000 * 60 * 60 * 24 // 24 hours
}

export const getMissingConsentForms = (
  assessment: PopulatedAssessment,
  hbConsentForms: Record<string, ConsentFormTemplate>,
  practiceConsentForms: Record<string, ConsentFormTemplate>,
  existingForms: WithId<ConsentForm>[],
) => {
  const { midwifeId: practiceId } = assessment
  const { hb, practice } = getDefaultConsentForms(
    assessment,
    practiceId ?? null,
    hbConsentForms,
    practiceConsentForms,
  )

  const practiceTemplateIds = practiceId
    ? practice.map(p => ({
        practiceId,
        form: p.form,
        conditions: p.conditions,
      }))
    : []
  const templateIds = [
    ...hb.map(h => ({
      form: h.form,
      conditions: h.conditions,
      practiceId: null,
    })),
    ...practiceTemplateIds,
  ]
  const existingRequiredConsentForms = existingForms.filter(form => {
    if (form.practiceId)
      return templateIds.find(
        t => t.form.id === form.consentFormTemplateId && t.practiceId === form.practiceId,
      )
    return (
      !!form.consentFormTemplateId &&
      templateIds.find(t => t.form.id === form.consentFormTemplateId)
    )
  })

  // Create a map of template IDs to existing form IDs
  const existingFormMap = new Map<string, string>()
  existingRequiredConsentForms.forEach(form => {
    if (form.consentFormTemplateId) {
      existingFormMap.set(
        form.practiceId
          ? `${form.practiceId}-${form.consentFormTemplateId}`
          : form.consentFormTemplateId,
        form.id,
      )
    }
  })

  const assessmentSentOn = getInitialSendDate(assessment)
  // Create an array of promises for each template ID
  const missingForms = templateIds.reduce<MissingConsentForm[]>((acc, formData) => {
    const existingFormId = existingFormMap.get(
      formData.practiceId ? `${formData.practiceId}-${formData.form.id}` : formData.form.id,
    )
    const existingForm = existingFormId ? existingForms.find(f => f.id === existingFormId) : null
    const shouldSendForm = getShouldSendForm(assessmentSentOn, existingForm)
    if (!shouldSendForm) {
      return acc
    }
    return [...acc, formData]
  }, [])

  return missingForms
}
export const getTempConsentFormStorageDir = (uid: string, isInvite: boolean) =>
  `${isInvite ? USER_INVITES : USERS}/${uid}/consent-forms-temp`
export const getTempConsentFormStoragePath = (
  uid: string,
  isInvite: boolean,
  consentFormId: string,
  now: number,
) => `${getTempConsentFormStorageDir(uid, isInvite)}/${consentFormId}-${now}.pdf`
